/** The live mobs, missiles and crates alike, keyed by the server's id. */
import type {
  EventRepelMob,
  PlayerFireProjectile,
  ServerEventLeaveHorizonMessage,
  ServerMobDespawnCoordsMessage,
  ServerMobDespawnMessage,
  ServerMobUpdateMessage,
  ServerMobUpdateStationaryMessage,
  ServerPlayerHitMessage,
} from '@airmash/protocol';
import { MOB_TYPES, SERVER_PACKETS } from '@airmash/protocol';
import { clock } from './Clock';
import { Mob } from './Mob';
import { explosion as particlesExplosion } from './particles/Particles';
import { mobExplosion as soundMobExplosion } from './Sound';
import { rand } from './Tools';
import type Vector from './Vector';

const mobs = new Map<number, Mob>();

export function add(
  data: PlayerFireProjectile | ServerMobUpdateMessage | ServerMobUpdateStationaryMessage | EventRepelMob,
  network: boolean,
  ownerId?: number,
) {
  // Destroy existing mob with the same ID to prevent orphaned sprites.
  // Can happen when PLAYER_FIRE and MOB_UPDATE arrive for the same missile.
  const existing = mobs.get(data.id);
  if (existing) {
    existing.destroy();
  }

  const mob = new Mob(data, ownerId);
  mobs.set(data.id, mob);
  if (network) {
    mob.network(data);
  }
}

export function update() {
  for (const mob of mobs.values()) {
    mob.update(clock.timeFactor);

    if (mob.forDeletion) {
      // Destroy the mob directly rather than via destroy() which re-fetches
      // from the Map by ID — if a new mob with the same ID was added between
      // despawn and this frame, the re-fetch would destroy the wrong mob.
      mob.destroy();
      mobs.delete(mob.id);
    } else {
      mob.updateGraphics();
    }
  }
}

export function network(
  data: ServerMobUpdateMessage | ServerMobUpdateStationaryMessage | EventRepelMob,
  ownerId?: number,
) {
  const mob = mobs.get(data.id);
  if (!mob) {
    add(data, true, ownerId);
  } else {
    mob.network(data, ownerId);
  }
}

export function despawn(despawnMsg: ServerMobDespawnMessage) {
  mobs.get(despawnMsg.id)?.despawn(despawnMsg.type);
}

/** Removes the mob the packet names. Only a despawn with coordinates explodes here: a hit's explosion
 *  comes from the player's side, and leaving the horizon is silent. */
export function destroy(data: ServerPlayerHitMessage | ServerEventLeaveHorizonMessage | ServerMobDespawnCoordsMessage) {
  const mob = mobs.get(data.id);
  if (mob) {
    mob.destroy();
    mobs.delete(data.id);
    if (data.c === SERVER_PACKETS.MOB_DESPAWN_COORDS) {
      explosion(mob.pos, data.type);
    }
  }
}

export function explosion(pos: Vector, mobType: MOB_TYPES) {
  switch (mobType) {
    case MOB_TYPES.PREDATOR_MISSILE:
    case MOB_TYPES.TORNADO_MISSILE:
    case MOB_TYPES.TORNADO_SMALL_MISSILE:
    case MOB_TYPES.PROWLER_MISSILE:
      particlesExplosion(pos, rand(1, 1.2));
      break;
    case MOB_TYPES.GOLIATH_MISSILE:
      particlesExplosion(pos, rand(1.3, 1.6));
      break;
    case MOB_TYPES.COPTER_MISSILE:
      particlesExplosion(pos, rand(0.8, 1));
      break;
  }
  soundMobExplosion(pos, mobType);
}

/** Returns [active, total] mob counts. */
export function count(): [number, number] {
  let totalCount = 0;
  let culledCount = 0;

  for (const mob of mobs.values()) {
    totalCount++;
    if (mob.culled) {
      culledCount++;
    }
  }

  return [totalCount - culledCount, totalCount];
}

export function wipe() {
  for (const mob of mobs.values()) {
    mob.destroy();
  }
  mobs.clear();
}
